/* eslint-disable max-lines */
import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import {
  SpacingBox,
  H3,
  H4,
  BigColors,
  Sizes,
  ParagraphSavingSpaceWhenEmpty,
  Dropdown,
  Caption,
  FlexBox,
} from '../../Components';
import {
  getProductColorsForNumber,
  getProductSizesForNumber,
  getDeliveryDataForProduct,
  getPriceForSizeForProductAndColor,
  getCurrentOrder,
  getProductPriceSummary,
  getTischOffensiveColors,
} from '../../selectors';
import { PriceSummary } from '../PriceSummary';
import { getTranslateByScope } from '../../i18n';
import AppContext from '../../AppContext';
import { formatMoney } from '../../utils/money';

import { getSearchParam } from '../../utils/routes';
import { productNr, productName } from '../../utils/products';
import { routes } from '../../routes';
import AddProductToOrderButton from './AddProductToOrderButton';

const translate = getTranslateByScope('app.productDetail');

export const Price = ({ price }) => (
    <ParagraphSavingSpaceWhenEmpty>
      {price ? formatMoney(price) : null}
    </ParagraphSavingSpaceWhenEmpty>
);

export const ProductToOrderBox = withRouter(
  ({ product, history, ...restProps }) => {
    const { actions, ...context } = useContext(AppContext);
    const marketing = !!context.state.marketing;

    const [deliveryDate, setDeliveryDate] = useState();

    const {
      includesDeliveryPeriod,
      defaultDeliveryDate,
      deliveryPeriodsMapped,
    } = getDeliveryDataForProduct(context, product.number);

    useEffect(() => {
      if (includesDeliveryPeriod) {
        setDeliveryDate(defaultDeliveryDate);
      }
    }, [includesDeliveryPeriod, setDeliveryDate, defaultDeliveryDate]);

    let colors = [];
    const allColors = getProductColorsForNumber(context)(
      product.number,
      deliveryDate,
    );
    colors = allColors;
    const tischOffensiveId = getSearchParam(restProps, 'tischOffensiveId') || null;
    if (tischOffensiveId) {
      const tColors = getTischOffensiveColors({context, id: tischOffensiveId});
      const currentColors = tColors[product.number];
      colors = colors.filter((a) => currentColors.includes(a.code));
    }

    colors = colors.sort();
    const sizes = getProductSizesForNumber(context)(
      product.number,
      deliveryDate,
    );

    let variant = translate('missing');

    if (product.material) {
      // eslint-disable-next-line prefer-destructuring
      variant = product.material.split('-')[1];
    }

    const currentOrder = getCurrentOrder(context);

    const color = getSearchParam(restProps, 'color') || colors[0];

    const { pricesForSizes, currency } = getPriceForSizeForProductAndColor(
      sizes,
      actions,
      context,
      color,
      product,
      currentOrder,
    );

    const productSummary = getProductPriceSummary({
      sizes: _.keys(pricesForSizes),
      pricesForSizes,
    });

    const clusterFormat = (cluster) => {
      if (!cluster) {
        return null;
      }

      return cluster.split('|').join(' | ')
    }

    return (
      <div style={{backgroundColor: 'white'}}>
        <Caption>{product.MaterialInfo__c}</Caption>
        <SpacingBox marginBottom="md">
          <H3>{productName(product)}</H3>
        </SpacingBox>

        <SpacingBox marginBottom="xl">
          {!marketing && (
            <>
            <SpacingBox>
              <b>{translate('cluster')}</b>
            </SpacingBox>
            <SpacingBox marginBottom="sm">
              {clusterFormat(product.cluster) || translate('missing')}
            </SpacingBox>
            </>
          )}
          <FlexBox marginBottom="sm">
            <SpacingBox>
              <b>{translate('collection')}</b>
            </SpacingBox>
            <SpacingBox marginLeft="sm">
              {product.collection || translate('missing')}
            </SpacingBox>
          </FlexBox>
          <FlexBox marginBottom="sm">
            <SpacingBox>
              <b>{translate('variant')}</b>
            </SpacingBox>
            <SpacingBox marginLeft="sm">{variant}</SpacingBox>
          </FlexBox>
          <FlexBox marginBottom="sm">
            <SpacingBox>
              <b>{translate('articleNumber')}</b>
            </SpacingBox>
            <SpacingBox marginLeft="sm">{product.number}</SpacingBox>
          </FlexBox>
          <FlexBox marginBottom="sm">
            <SpacingBox>
              <b>{translate('materialNumber')}</b>
            </SpacingBox>
            <SpacingBox marginLeft="sm">
              {product.material || translate('missing')}
            </SpacingBox>
          </FlexBox>
        </SpacingBox>
        <SpacingBox marginBottom="lg">
          <SpacingBox marginBottom="sm">
            <H4>{translate('deliveryDate')}</H4>
          </SpacingBox>

          {deliveryPeriodsMapped.length === 0 && (
            <div>{translate('noDeliveryDates')}</div>
          )}

          {deliveryPeriodsMapped.length > 0 && (
            <>
              {marketing && (
                <ul>
                  {deliveryPeriodsMapped.map((d) => (
                    <li>{d.label}</li>
                  ))}
                </ul>
              )}

              {!marketing && (
                <Dropdown
                  options={deliveryPeriodsMapped}
                  placeholder={translate('deliveryDatePlaceholder')}
                  onChange={setDeliveryDate}
                  value={deliveryDate}
                  noOptionsMessage={() => translate('noDeliveryDateFound')}
                  firstSelectedAsDefault
                />
              )}
            </>
          )}
        </SpacingBox>
        <SpacingBox marginBottom="lg">
          <SpacingBox marginBottom="sm">
            <H4>{translate('colors')}</H4>
          </SpacingBox>
          <BigColors
            onClick={(c) => history.replace(
              routes.productDetail({
                name: productNr(product),
                tischOffensiveId: tischOffensiveId,
                color: c,
              }),
            )}
            colors={colors}
          />
        </SpacingBox>
        <SpacingBox marginBottom="lg">
          <SpacingBox marginBottom="sm">
            <H4>{translate('sizes')}</H4>
          </SpacingBox>
          <Sizes sizes={sizes} />
        </SpacingBox>
        {!marketing && (
          <AddProductToOrderButton
            deliveryDate={deliveryDate}
            product={product}
            tischOffensiveId={tischOffensiveId}
          />
        )}

        {!marketing && (
          <div>
            <b>
{translate('priceSummary.title')}
:
            </b>
            <br />
            <PriceSummary values={productSummary} currency={currency} />
          </div>
        )}
      </div>
    );
  },
);
